import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileText, Sun, Container, Users, CheckCircle2, FileEdit } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { StatusBadge } from '../components/common/Badges';
import api from '../lib/api';
import { fmtMoney, fmtDate } from '../lib/format';

const KPI = ({ icon: Icon, label, value, accent, testid }) => (
  <Card data-testid={testid} className="border-border shadow-[var(--shadow-elev-1)]">
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`h-11 w-11 rounded-lg flex items-center justify-center border ${accent}`}>
        <Icon size={20} />
      </div>
      <div>
        <div className="text-2xl font-semibold font-mono">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </CardContent>
  </Card>
);

export default function DashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => (await api.get('/dashboard/stats')).data,
  });

  if (isLoading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  const t = data?.totales || {};
  const montoMXN = data?.montos_por_moneda?.MXN || 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Panel de control</h2>
        <p className="text-sm text-muted-foreground">Resumen general de cotizaciones y actividad</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={FileText} label="Cotizaciones Prefab" value={t.prefab ?? 0} testid="kpi-prefab" accent="bg-primary/10 border-primary/25 text-primary" />
        <KPI icon={Sun} label="Cotizaciones PV" value={t.pv ?? 0} testid="kpi-pv" accent="bg-[hsl(var(--warning)/0.12)] border-[hsl(var(--warning)/0.3)] text-[hsl(var(--warning))]" />
        <KPI icon={Container} label="Contenedores" value={t.contenedores ?? 0} testid="kpi-contenedores" accent="bg-[hsl(var(--info)/0.12)] border-[hsl(var(--info)/0.3)] text-[hsl(var(--info))]" />
        <KPI icon={Users} label="Clientes" value={t.clientes ?? 0} testid="kpi-clientes" accent="bg-muted border-border text-muted-foreground" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-border">
          <CardContent className="p-5 flex items-center gap-4">
            <CheckCircle2 size={20} className="text-primary" />
            <div>
              <div className="text-xl font-semibold font-mono" data-testid="kpi-finalizadas">{t.finalizadas ?? 0}</div>
              <div className="text-xs text-muted-foreground">Finalizadas</div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-5 flex items-center gap-4">
            <FileEdit size={20} className="text-muted-foreground" />
            <div>
              <div className="text-xl font-semibold font-mono" data-testid="kpi-borradores">{t.borradores ?? 0}</div>
              <div className="text-xs text-muted-foreground">En borrador</div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="text-xl font-semibold money" data-testid="kpi-monto-total">{fmtMoney(montoMXN, 'MXN')}</div>
            <div className="text-xs text-muted-foreground text-right">Monto total cotizado (MXN)</div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border shadow-[var(--shadow-elev-1)]">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Cotizaciones recientes</CardTitle>
        </CardHeader>
        <CardContent>
          <Table data-testid="recent-quotes-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="th-label">Número</TableHead>
                <TableHead className="th-label">Nombre</TableHead>
                <TableHead className="th-label">Cliente</TableHead>
                <TableHead className="th-label">Estado</TableHead>
                <TableHead className="th-label text-right">Total</TableHead>
                <TableHead className="th-label">Actualizada</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.recientes || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Sin cotizaciones aún. Crea la primera desde “Cotizaciones Prefab”.
                  </TableCell>
                </TableRow>
              )}
              {(data?.recientes || []).map((q) => (
                <TableRow
                  key={q.id}
                  className="cursor-pointer hover:bg-background/40"
                  onClick={() => navigate(`/cotizaciones/${q.id}/editor`)}
                  data-testid={`recent-quote-row-${q.quotation_number}`}
                >
                  <TableCell className="font-mono text-[13px]">{q.quotation_number}</TableCell>
                  <TableCell className="max-w-[280px] truncate">{q.nombre}</TableCell>
                  <TableCell className="text-muted-foreground">{q.cliente_nombre}</TableCell>
                  <TableCell><StatusBadge status={q.status} /></TableCell>
                  <TableCell className="money">{fmtMoney(q.totals?.total, q.moneda)}</TableCell>
                  <TableCell className="text-muted-foreground text-[13px]">{fmtDate(q.updated_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
